export const CLASS_PHOTO_MAX_PHRASE_CHARS = 40;

export const CLASS_PHOTO_PHRASE_EXAMPLES = [
  {
    label: '졸업 축하',
    phrase: '졸업을 축하합니다!',
    instruction: '졸업모와 꽃다발 장식을 더하고 밝고 따뜻한 분위기로 만들어주세요.',
  },
  {
    label: '우리 반 최고',
    phrase: '우리 반 최고!',
    instruction: '별과 하트 장식을 더하고 밝고 신나는 분위기로 만들어주세요.',
  },
  {
    label: '함께한 1년',
    phrase: '함께한 1년, 감사합니다',
    instruction: '칠판과 책상이 있는 따뜻한 교실 배경으로 만들어주세요.',
  },
  {
    label: '사랑해요',
    phrase: '선생님 사랑해요',
    instruction: '하트가 날리는 장식과 분홍색·하늘색의 따뜻한 색감을 사용해주세요.',
  },
];

const CLASS_PHOTO_IDENTITY_BLOCK = `IDENTITY AND GROUP INTEGRITY:
- Preserve the exact number of visible people. Do not add, remove, merge, duplicate, or crop anyone out.
- Keep the original left-to-right arrangement and relative positions so the group remains recognizable.
- Preserve each person's face shape, hairstyle, glasses, expression, clothing colors, and other distinguishing features.
- Do not add glasses, hats, or accessories that are not visible in the photo.
- Every face must remain distinct and recognizable rather than becoming a generic repeated character.`;

const CLASS_PHOTO_TITLE_CONTRACT_HEAD = `EXACT KOREAN TITLE:
- The user's desired phrase may appear inside <EXACT_KOREAN_TEXT> tags or naturally in the request.
- If tags are present, render the tagged Korean phrase as part of the artwork exactly as provided.
- For a natural request, render only the Korean title the teacher explicitly asks to put in the image. If no title is requested, add no text.
- Never translate, paraphrase, correct, add, remove, or repeat a requested character.`;

/**
 * 광고(Meta v3 캐러셀)와 이름·예시가 1:1로 일치하는 4개 스타일 프리셋.
 * 픽셀·크레파스·피규어의 스타일/문구 통합 지시는 v3 소재 제작에서
 * 실제 단체사진 + 한글 문구 렌더링까지 검증된 프롬프트를 기반으로 한다.
 */
export const CLASS_PHOTO_STYLE_PRESETS = [
  {
    id: 'storybook',
    name: '동화 일러스트',
    badge: '기본',
    thumbnail: '/images/events/class-photo-style-storybook.webp',
    thumbnailAlt: '동화 일러스트 스타일로 그린 가상 학급 예시',
    promptInstruction: '밝고 따뜻한 우리반 카툰 일러스트로 바꿔주세요',
    header: 'Transform the uploaded class group photo into a vibrant, warm cartoon-style class illustration.',
    compositionLines: [
      '- Use a zoomed-out group composition with every person fully visible.',
      '- Reserve the top 22% as a clean title-safe area. Keep every head and face below that area.',
      '- Fill a square frame with sharp corners and extend the classroom scenery naturally to the edges.',
      '- Keep the warm, cheerful atmosphere of a Korean classroom memory photo.',
    ],
    styleLines: [
      '- Bright, colorful, polished cartoon/illustration style with clean outlines.',
      '- Kid-friendly and celebratory, suitable for a classroom display, class profile image, or keepsake.',
      '- Small confetti, banner, flower, or heart accents are allowed only when they do not cover a person.',
    ],
    titleLines: [
      '- Default position: top-center in the reserved title-safe area.',
      '- Font style: cute, rounded, bold Korean display lettering.',
      '- Colors: a vibrant coral, yellow, and blue mix with a THICK WHITE OUTLINE and a subtle shadow.',
    ],
    titleIntegrationLine: '- Integrate small matching stars, hearts, confetti, flowers, or a ribbon around the title so it feels designed together with the illustration.',
  },
  {
    id: 'pixel',
    name: '픽셀아트',
    thumbnail: '/images/events/class-photo-style-pixel.webp',
    thumbnailAlt: '픽셀아트 스타일로 그린 가상 학급 예시',
    promptInstruction: '우리반을 아기자기한 도트 픽셀아트 그림으로 바꿔주세요',
    header: 'Transform the uploaded class group photo into a cute retro pixel art class illustration.',
    compositionLines: [
      '- Use a zoomed-out group composition with every person fully visible.',
      '- Reserve the top 22% as a clean title-safe area. Keep every head and face below that area.',
      '- Fill a square frame with sharp corners and extend the pixel-art classroom scenery naturally to the edges.',
      '- Keep the warm, cheerful atmosphere of a Korean classroom rendered fully in pixel art.',
    ],
    styleLines: [
      '- Cute pixel art in the style of Everskies avatars and Stardew Valley character portraits: chunky visible pixels, clean sprite outlines, and expressive pixel faces.',
      '- Warm cozy pixel-art lighting like Stardew Valley interiors, with the classroom background (chalkboard, shelves, window light) rendered in the same pixel art style.',
      '- A subtle small game-style UI strip is allowed along the bottom edge only, and it must never cover a person.',
    ],
    titleLines: [
      '- Position: on a pixel-art wooden sign banner across the top title-safe area.',
      '- Font style: bold pixel-style Korean lettering with every Hangul syllable block formed correctly and clearly legible.',
      '- Colors: dark brown pixel letters on the light wooden banner.',
    ],
    titleIntegrationLine: '- Integrate the banner into the scene like a hand-made pixel sign so it feels part of the same game world.',
  },
  {
    id: 'crayon',
    name: '크레파스 아이그림',
    thumbnail: '/images/events/class-photo-style-crayon.webp',
    thumbnailAlt: '크레파스 아이그림 스타일로 그린 가상 학급 예시',
    promptInstruction: '우리반을 아이가 크레파스로 그린 그림처럼 바꿔주세요',
    header: 'Transform the uploaded class group photo into an endearing crayon drawing that looks drawn by a young child.',
    compositionLines: [
      '- Use a zoomed-out group composition with every person fully visible.',
      '- Reserve the top 22% as a clean title-safe area. Keep every head and face below that area.',
      '- Fill a square frame with sharp corners and extend the drawing-paper background naturally to the edges.',
      "- Keep the warm, proud feel of a child's drawing of their own class.",
    ],
    styleLines: [
      '- Drawn as if by a six-year-old child using thick wax crayons: wobbly bold crayon outlines, coloring that spills outside the lines, and visible waxy crayon texture on slightly off-white drawing paper.',
      '- STYLE PRIORITY: the child-drawing simplification wins over realistic facial detail. Every face becomes simple dot eyes, a tiny nose, and a single curved smile line; identity stays recognizable through hairstyle, glasses, and clothing colors rather than facial realism.',
      '- Slightly oversized round heads on small simple bodies with wobbly, uneven proportions, a pink circular blush scribble on every cheek, and bright primary colors.',
      '- Endearingly clumsy kindergarten-drawing feel; a few crayon scribble stars or a wobbly sun are allowed only when they do not cover a person.',
    ],
    titleLines: [
      '- Position: across the top title-safe area.',
      '- Font style: wobbly but clearly legible child-like crayon handwriting with every Hangul syllable block formed correctly.',
      '- Colors: each Hangul syllable in a different bright crayon color.',
    ],
    titleIntegrationLine: "- Integrate the lettering as part of the same child's drawing, slightly uneven but proudly written.",
  },
  {
    id: 'figurine',
    name: '미니어처 피규어',
    thumbnail: '/images/events/class-photo-style-figurine.webp',
    thumbnailAlt: '미니어처 피규어 스타일로 만든 가상 학급 예시',
    promptInstruction: '우리반을 미니어처 피규어 세트를 찍은 사진처럼 바꿔주세요',
    header: 'Transform the uploaded class group photo into a realistic photograph of a cute collectible miniature figurine set of the whole class.',
    compositionLines: [
      '- Use a zoomed-out product-photography composition with the whole figurine set fully visible.',
      '- Keep a small clear area at the front edge of the display base for a name plaque.',
      '- Fill a square frame with sharp corners and extend the desk-top scene naturally to the edges.',
      '- Keep the warm, collectible-display feel of a treasured class keepsake.',
    ],
    styleLines: [
      '- Every person becomes a small glossy PVC figurine with cute chibi proportions, standing or seated together on one shared round transparent acrylic display base.',
      '- The set is displayed on a wooden desk in soft window daylight with tiny miniature classroom props (a tiny green chalkboard, a tiny school desk and chair) around it.',
      '- Photorealistic product-photography look with shallow depth of field and warm tones.',
    ],
    titleLines: [
      '- Position: on a small neat wooden desk plaque standing at the front edge of the acrylic display base.',
      '- Font style: clean, accurate Korean typography, sharp and legible, with every Hangul syllable block formed correctly.',
      '- Colors: dark engraved letters on the light wood plaque.',
    ],
    titleIntegrationLine: '- Integrate the plaque as a real physical object in the scene, matching the lighting and perspective of the figurine set.',
  },
];

export const CLASS_PHOTO_DEFAULT_STYLE_ID = 'storybook';

export const getClassPhotoStylePreset = (styleId) => (
  CLASS_PHOTO_STYLE_PRESETS.find((preset) => preset.id === styleId)
  || CLASS_PHOTO_STYLE_PRESETS.find(
    (preset) => preset.id === CLASS_PHOTO_DEFAULT_STYLE_ID,
  )
);

export const normalizeClassPhotoStyleId = (value) => (
  getClassPhotoStylePreset(String(value || '').trim()).id
);

export const buildClassPhotoSystemPrompt = (styleId) => {
  const preset = getClassPhotoStylePreset(styleId);
  return [
    preset.header,
    '',
    CLASS_PHOTO_IDENTITY_BLOCK,
    '',
    'COMPOSITION:',
    ...preset.compositionLines,
    '',
    'STYLE:',
    ...preset.styleLines,
    '',
    CLASS_PHOTO_TITLE_CONTRACT_HEAD,
    ...preset.titleLines,
    '- Size: prominent and clearly readable, but balanced with the group portrait.',
    preset.titleIntegrationLine,
    '- Do not render the tags themselves. Do not add any other text, letters, numbers, logos, or watermark.',
    '- Before finalizing, visually verify that the title matches every Korean character between the tags.',
  ].join('\n');
};

export const CLASS_PHOTO_SYSTEM_PROMPT = buildClassPhotoSystemPrompt(
  CLASS_PHOTO_DEFAULT_STYLE_ID,
);

export const normalizeClassPhotoPhrase = (value) => (
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CLASS_PHOTO_MAX_PHRASE_CHARS)
);

export const extractClassPhotoPhraseFromPrompt = (value) => {
  const prompt = String(value || '').replace(/\s+/g, ' ').trim().slice(0, 500);
  if (!prompt) return '';
  const patterns = [
    /["“”]([^"“”]{1,40})["“”]/,
    /'([^']{1,40})'/,
    /(.{1,40}?)(?:라는|이라고|라고)\s*(?:문구|글자|제목)/,
    /(?:문구|글자|제목)(?:는|로|:)?\s*(.{1,40}?)(?=\s*(?:을|를|이라고|라고)?\s*(?:넣|적|써|표현|추가))/,
  ];
  for (const pattern of patterns) {
    const match = prompt.match(pattern);
    if (match?.[1]) return normalizeClassPhotoPhrase(match[1]);
  }
  return '';
};

export const buildClassPhotoModelPrompt = ({ instruction, phrase, styleId }) => {
  const preset = getClassPhotoStylePreset(styleId);
  const normalizedPhrase = normalizeClassPhotoPhrase(phrase);
  const trimmedInstruction = String(instruction || '').trim();
  const instructionLines = trimmedInstruction
    && trimmedInstruction !== preset.promptInstruction
    ? [preset.promptInstruction, trimmedInstruction]
    : [preset.promptInstruction];
  return [
    ...instructionLines,
    '',
    'Create one finished class profile artwork with the exact Korean title below integrated into the illustration.',
    '<EXACT_KOREAN_TEXT>',
    normalizedPhrase,
    '</EXACT_KOREAN_TEXT>',
    'Copy every Korean character exactly. Do not add any other text.',
  ].join('\n');
};
